/**
 * useRemoteConfig - Hook for Firebase Remote Config feature flags.
 *
 * Uses a global store to fetch config ONCE at app startup, then
 * all components read from the store. This prevents:
 * - Multiple fetch requests
 * - UI flickering from false -> true transitions
 *
 * Call initializeRemoteConfig() once at app startup (in index.tsx).
 */

import { useMemo } from 'react';
import { Store } from 'pullstate';
import { fetchAndActivate, getValue } from 'firebase/remote-config';
import { remoteConfig } from '../config/firebase.config';

// ============================================================================
// Remote Config Store
// ============================================================================

interface RemoteConfigState {
  isLoaded: boolean;
  // Plaid feature flags
  plaidPortfolioEnabled: boolean;
  // Plaid cache config
  plaidCacheTtlMinutes: number;
  plaidMaxDailyRefreshes: number;
  plaidCacheEnabled: boolean;
}

// Initialize with default values from firebase.config.ts
const RemoteConfigStore = new Store<RemoteConfigState>({
  isLoaded: false,
  plaidPortfolioEnabled: false,
  plaidCacheTtlMinutes: 240,
  plaidMaxDailyRefreshes: 5,
  plaidCacheEnabled: true,
});

// Track if initialization has been started (singleton pattern)
let initializationStarted = false;

/**
 * Initialize Remote Config - call once at app startup.
 * Fetches config from Firebase and updates the store.
 * Safe to call multiple times (will only fetch once).
 */
export const initializeRemoteConfig = async (): Promise<void> => {
  // Prevent multiple initializations
  if (initializationStarted) {
    return;
  }
  initializationStarted = true;

  const updateStoreFromRemoteConfig = () => {
    const plaidKey = import.meta.env.PROD ? 'plaid_portfolio_prd' : 'plaid_portfolio_dev';
    const plaidEnabled = getValue(remoteConfig, plaidKey).asBoolean();
    RemoteConfigStore.update((s) => {
      s.isLoaded = true;
      s.plaidPortfolioEnabled = plaidEnabled;
      s.plaidCacheTtlMinutes = getValue(remoteConfig, 'plaid_cache_ttl_minutes').asNumber();
      s.plaidMaxDailyRefreshes = getValue(remoteConfig, 'plaid_max_daily_refreshes').asNumber();
      s.plaidCacheEnabled = getValue(remoteConfig, 'plaid_cache_enabled').asBoolean();
    });

    if (import.meta.env.DEV) {
      console.log(`[RemoteConfig] Loaded: ${plaidKey} = ${plaidEnabled}`);
    }
  };

  // Start fetch and update the store when it resolves — even if it completes after the timeout.
  // This allows the app to self-correct: defaults are used immediately on slow networks,
  // but real values are applied once the fetch eventually succeeds.
  const fetchPromise = fetchAndActivate(remoteConfig)
    .then(() => {
      updateStoreFromRemoteConfig();
    })
    .catch((error) => {
      console.error('[RemoteConfig] Fetch failed:', error);
      // Only fall back to defaults if the timeout hasn't already marked us as loaded
      RemoteConfigStore.update((s) => {
        if (!s.isLoaded) {
          s.isLoaded = true;
        }
      });
    });

  // Timeout: unblock startup after 8s, using defaults if fetch hasn't completed yet
  const timeoutPromise = new Promise<void>((resolve) =>
    setTimeout(() => {
      RemoteConfigStore.update((s) => {
        if (!s.isLoaded) {
          s.isLoaded = true;
        }
      });
      if (import.meta.env.DEV) {
        console.warn('[RemoteConfig] Fetch timed out after 8s, using defaults (fetch continues in background)');
      }
      resolve();
    }, 8000),
  );

  // Await whichever completes first; the fetch continues in the background regardless
  await Promise.race([fetchPromise, timeoutPromise]);
};

// ============================================================================
// Hooks - Read from store (no fetching)
// ============================================================================

/**
 * Hook specifically for the Plaid portfolio feature flag.
 * Uses plaid_portfolio_prd in production, plaid_portfolio_dev in development.
 * Returns true if Plaid UI should be shown, false otherwise.
 */
export const usePlaidFeatureFlag = (): boolean => {
  const { plaidPortfolioEnabled } = RemoteConfigStore.useState((s) => ({
    plaidPortfolioEnabled: s.plaidPortfolioEnabled,
  }));

  return plaidPortfolioEnabled;
};

/**
 * Hook for Plaid cache configuration from Remote Config.
 * Allows adjusting cache behavior without app releases.
 */
export interface PlaidCacheConfig {
  ttlMinutes: number;
  maxDailyRefreshes: number;
  cacheEnabled: boolean;
}

export const usePlaidCacheConfig = (): PlaidCacheConfig => {
  const config = RemoteConfigStore.useState((s) => ({
    ttlMinutes: s.plaidCacheTtlMinutes,
    maxDailyRefreshes: s.plaidMaxDailyRefreshes,
    cacheEnabled: s.plaidCacheEnabled,
  }));

  // Memoize to prevent unnecessary re-renders
  return useMemo(
    () => ({
      ttlMinutes: config.ttlMinutes,
      maxDailyRefreshes: config.maxDailyRefreshes,
      cacheEnabled: config.cacheEnabled,
    }),
    [config.ttlMinutes, config.maxDailyRefreshes, config.cacheEnabled],
  );
};

/**
 * Hook to check if Remote Config has been loaded.
 * Useful for showing loading states before config is ready.
 */
export const useRemoteConfigLoaded = (): boolean => {
  return RemoteConfigStore.useState((s) => s.isLoaded);
};
